import { Capacitor } from '@capacitor/core'
import * as Sentry from '@sentry/vue'
import { i18n } from '@/i18n'
import { openMemoryDatabase } from './memory-database'

// ============================================================================
// SECTION 1 — Singleton connection
// ============================================================================

let pluginInstance = null
let sqliteNamespace = null
let sqliteConnection = null
let webComponentsReady = false
let connection = null
let initPromise = null
// Set when the device database cannot start for this run of the app (see openFallbackDatabase).
let fallbackKind = null
const DB_BASENAME = 'app_data'
const DB_FILENAME = `${DB_BASENAME}.db`

// Write lock — serializes writes, reads are concurrent
let writeLock = Promise.resolve()

const withWriteLock = (fn) => {
    const task = writeLock.then(() => fn(), () => fn())
    writeLock = task.catch(() => {})
    return task
}

// Per-operation timeout — prevents permanent freeze if native bridge hangs
const OP_TIMEOUT_MS = 15_000
const LOCAL_STORAGE_NOT_READY_CODE = 'SQLITE_LOCAL_STORAGE_NOT_READY'
const LOCAL_STORAGE_NOT_READY_MESSAGE_KEY = 'error.local-storage-not-ready'

function withTimeout(promise, label = 'SQLite op') {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(`[SQLite] ${label} timed out after ${OP_TIMEOUT_MS}ms`)), OP_TIMEOUT_MS)
        promise.then(
            (val) => { clearTimeout(timer); resolve(val) },
            (err) => { clearTimeout(timer); reject(err) },
        )
    })
}

async function ensureJeepSqliteElement() {
    if (webComponentsReady || Capacitor.isNativePlatform()) return
    if (typeof window === 'undefined') return

    try {
        const { applyPolyfills, defineCustomElements } = await import('jeep-sqlite/loader')
        if (typeof applyPolyfills === 'function') await applyPolyfills()
        if (typeof defineCustomElements === 'function') await defineCustomElements(window)
    } catch (error) {
        console.warn('[SQLite] Failed to load jeep-sqlite', error)
        throw error
    }

    if (!document.querySelector('jeep-sqlite')) {
        const el = document.createElement('jeep-sqlite')
        el.style.display = 'none'
        document.body.appendChild(el)
    }

    try {
        if (typeof customElements !== 'undefined') {
            await customElements.whenDefined('jeep-sqlite')
        }
    } catch {}

    webComponentsReady = true
}

function getErrorMessage(error) {
    return String(error?.message || error || '').toLowerCase()
}

function isConnectionAlreadyExistsError(error) {
    return getErrorMessage(error).includes('already exists')
}

function isWebStoreNotOpenError(error) {
    const msg = getErrorMessage(error)
    return (
        (msg.includes('webstore') && msg.includes('not open')) ||
        (msg.includes('web store') && msg.includes('not open')) ||
        msg.includes('initwebstore')
    )
}

function normalizeSqliteError(error) {
    if (!isWebStoreNotOpenError(error)) return error

    const safeError = new Error(i18n.global.t(LOCAL_STORAGE_NOT_READY_MESSAGE_KEY))
    safeError.code = LOCAL_STORAGE_NOT_READY_CODE
    safeError.messageKey = LOCAL_STORAGE_NOT_READY_MESSAGE_KEY
    safeError.originalError = error
    return safeError
}

// The Android plugin fails to load when its Keystore-backed preferences cannot be decrypted (a
// backup restored on a new phone, a Keystore reset): every later call then rejects with its load
// message, prefixed like this, until the app data is cleared. A plugin missing from the build
// answers UNIMPLEMENTED. Neither recovers while the app runs.
const PLUGIN_LOAD_FAILURE_PREFIX = 'capacitorsqliteplugin:'

function isStorageUnavailableError(error) {
    if (error?.storageUnavailable || error?.code === 'UNIMPLEMENTED') return true
    const msg = getErrorMessage(error)
    return msg.startsWith(PLUGIN_LOAD_FAILURE_PREFIX) || msg.includes('not implemented on')
}

// What has to work before a connection can open (the plugin, the web component, the web store):
// a failure there means local storage is unavailable, not a stale connection.
async function setUpStorage(step) {
    try {
        return await step()
    } catch (error) {
        const unavailable = error instanceof Error ? error : new Error(String(error))
        unavailable.storageUnavailable = true
        throw unavailable
    }
}

// Instead of rejecting every read and write for the whole session, the app keeps its local data in
// memory until it closes: the session sync, the recordings and their uploads still work, only the
// offline copy does not outlive the app. Reported once, as the one event of this run.
async function openFallbackDatabase(cause) {
    const { connection: fallback, kind } = await openMemoryDatabase()
    fallbackKind = kind
    console.warn(`[SQLite] Local database unavailable, keeping local data in ${kind} storage this session`, cause)
    const report = new Error(`[SQLite] Local database unavailable, running on ${kind} storage for this session`, {
        cause,
    })
    Sentry.captureException(report, { level: 'warning', tags: { sqlite_fallback: kind } })
    return fallback
}

/** Whether the app runs on a stand-in database this session ('memory', 'empty') or not (null). */
export function getLocalStorageFallback() {
    return fallbackKind
}

async function ensureConnectionOpen(conn) {
    if (!conn) {
        throw new Error(`[SQLite] Connection ${DB_BASENAME} not available`)
    }

    const isOpen = typeof conn.isDBOpen === 'function'
        ? await conn.isDBOpen().catch(() => null)
        : null

    if (!isOpen?.result) {
        await conn.open()
    }
    return conn
}

async function recreateConnection({ closeExisting = false } = {}) {
    if (closeExisting && sqliteConnection?.closeConnection) {
        await sqliteConnection.closeConnection(DB_FILENAME, false).catch(() => null)
    }

    if (sqliteConnection?.checkConnectionsConsistency) {
        await sqliteConnection.checkConnectionsConsistency().catch(() => null)
    }

    const conn = await sqliteConnection.createConnection(DB_FILENAME, false, 'no-encryption', 1, false)
    return ensureConnectionOpen(conn)
}

async function openConnection() {
    const hasConnection = await sqliteConnection.isConnection(DB_FILENAME, false).catch(() => null)
    if (hasConnection?.result) {
        try {
            const conn = await sqliteConnection.retrieveConnection(DB_FILENAME, false)
            return ensureConnectionOpen(conn)
        } catch (error) {
            if (!isStaleConnectionError(error)) throw error
            console.warn('[SQLite] Stored connection became unavailable, recreating it')
            return recreateConnection({ closeExisting: true })
        }
    }

    try {
        const conn = await sqliteConnection.createConnection(DB_FILENAME, false, 'no-encryption', 1, false)
        return ensureConnectionOpen(conn)
    } catch (error) {
        if (isConnectionAlreadyExistsError(error)) {
            console.warn('[SQLite] Connection already exists natively, resetting registry and recreating it')
            return recreateConnection()
        }
        throw error
    }
}

/**
 * Returns the singleton database connection.
 * Safe to call concurrently — all callers share the same init promise.
 * initPromise persists after success (never reset) to prevent race conditions.
 */
export async function getDb() {
    if (connection) return connection

    // Shared promise — never reset on success, only on error
    if (initPromise) return initPromise

    initPromise = (async () => {
        try {
            await setUpStorage(async () => {
                await ensureJeepSqliteElement()

                // Load plugin
                const module = await import('@capacitor-community/sqlite')
                pluginInstance = module.CapacitorSQLite || module.sqlite || module.default
                sqliteNamespace = module
                sqliteConnection = new module.SQLiteConnection(pluginInstance)

                if (!pluginInstance) throw new Error('[SQLite] Plugin not available')

                // Web store (non-native only)
                if (!Capacitor.isNativePlatform()) {
                    await sqliteConnection.initWebStore()
                }
            })

            connection = await openConnection()
        } catch (error) {
            if (!isStorageUnavailableError(error)) throw error
            connection = await openFallbackDatabase(error)
        }
        return connection
    })()

    try {
        return await initPromise
    } catch (error) {
        // Reset on error so retry is possible
        initPromise = null
        connection = null
        pluginInstance = null
        sqliteNamespace = null
        sqliteConnection = null
        throw error
    }
}

/**
 * Detect stale/dead connection errors and force reconnection on next call.
 * iOS may invalidate native connections after background suspend.
 */
function isStaleConnectionError(error) {
    const msg = getErrorMessage(error)
    return (
        isWebStoreNotOpenError(error) ||
        (msg.includes('connection') && (
            msg.includes('closed') ||
            msg.includes('not open') ||
            msg.includes('no such connection') ||
            msg.includes('database not open') ||
            msg.includes('not available') ||
            msg.includes('does not exist')
        )) ||
        (msg.includes(`database ${DB_BASENAME}`) && msg.includes('not opened')) ||
        (msg.includes('no available connection for database') && msg.includes(DB_BASENAME))
    )
}

function resetConnection() {
    // The stand-in database holds this session's data: dropping it would lose it.
    if (fallbackKind) return
    console.warn('[SQLite] Stale connection detected — will reconnect on next call')
    connection = null
    initPromise = null
    pluginInstance = null
    sqliteNamespace = null
    sqliteConnection = null
}

async function runWithReconnect(operation) {
    let lastError

    for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
            return await operation()
        } catch (error) {
            lastError = error
            if (!isStaleConnectionError(error)) throw normalizeSqliteError(error)
            resetConnection()
        }
    }

    throw normalizeSqliteError(lastError)
}

/** Run a SQL write statement (serialized via write lock). */
export async function dbRun(sql, params = []) {
    return withWriteLock(async () => {
        return runWithReconnect(async () => {
            const db = await getDb()
            return await withTimeout(db.run(sql, params), 'dbRun')
        })
    })
}

/** Run a SQL read query (no lock — concurrent reads allowed). */
export async function dbQuery(sql, params = []) {
    return runWithReconnect(async () => {
        const db = await getDb()
        return await withTimeout(db.query(sql, params), 'dbQuery')
    })
}

/** Execute raw SQL statements (serialized via write lock). */
export async function dbExecute(sql) {
    return withWriteLock(async () => {
        return runWithReconnect(async () => {
            const db = await getDb()
            return await withTimeout(db.execute(sql), 'dbExecute')
        })
    })
}

/** The column names of a table (PRAGMA table_info), as a Set. */
export async function readTableColumns(tableName) {
    const result = await dbQuery(`PRAGMA table_info(${tableName});`)
    return new Set((result?.values ?? []).map((row) => row.name))
}

/** Whether a table of that exact name exists (sqlite_master). */
export async function tableExists(tableName) {
    const result = await dbQuery("SELECT name FROM sqlite_master WHERE type = 'table' AND name = ? LIMIT 1;", [
        tableName,
    ])
    return Boolean(result?.values?.length)
}

export async function persistWebStore() {
    if (Capacitor.isNativePlatform()) return

    return withWriteLock(async () => {
        try {
            await runWithReconnect(async () => {
                await getDb()
                if (fallbackKind || !sqliteConnection?.saveToStore) return
                await withTimeout(sqliteConnection.saveToStore(DB_BASENAME), 'saveToStore')
            })
        } catch (error) {
            console.warn('[SQLite] Failed to persist web store', error)
            throw error
        }
    })
}

/** Alias for getDb() — backward compatibility. */
export async function initSQLite() {
    await runWithReconnect(() => getDb())
}

/** Access the raw @capacitor-community/sqlite module. */
export function getSQLiteNamespace() {
    if (!sqliteNamespace) throw new Error('[SQLite] Plugin not initialised')
    return sqliteNamespace
}

// ============================================================================
// SECTION 2 — KV Store
// ============================================================================

let kvSchemaReady = false
let kvSchemaPromise = null

async function ensureKvSchema() {
    if (kvSchemaReady) return
    if (kvSchemaPromise) return kvSchemaPromise

    kvSchemaPromise = (async () => {
        await dbExecute(`
            CREATE TABLE IF NOT EXISTS kv_store (
                key TEXT PRIMARY KEY,
                value TEXT,
                updated_at INTEGER NOT NULL DEFAULT (strftime('%s','now'))
            );
            CREATE INDEX IF NOT EXISTS idx_kv_store_updated_at ON kv_store(updated_at);
        `)

        kvSchemaReady = true
    })()
        .catch((error) => { throw error })
        .finally(() => { kvSchemaPromise = null })

    return kvSchemaPromise
}

export async function setItem(key, value) {
    await ensureKvSchema()
    const serialized = JSON.stringify(value)
    const now = Math.floor(Date.now() / 1000)
    await dbRun(
        `INSERT INTO kv_store(key, value, updated_at)
         VALUES(?, ?, ?)
         ON CONFLICT(key) DO UPDATE SET
            value = excluded.value,
            updated_at = excluded.updated_at;`,
        [key, serialized, now]
    )
}

export async function getItem(key, defaultValue = null) {
    await ensureKvSchema()
    const result = await dbQuery('SELECT value FROM kv_store WHERE key = ? LIMIT 1;', [key])
    if (!result?.values?.length) return defaultValue
    try {
        return JSON.parse(result.values[0].value)
    } catch {
        return defaultValue
    }
}

export async function removeItem(key) {
    await ensureKvSchema()
    await dbRun('DELETE FROM kv_store WHERE key = ?;', [key])
}

export async function clearStore() {
    await ensureKvSchema()
    await dbRun('DELETE FROM kv_store;')
}

export async function listEntries({ prefix = '' } = {}) {
    await ensureKvSchema()
    const like = prefix ? `${prefix}%` : '%'
    const result = await dbQuery(
        'SELECT key, value, updated_at FROM kv_store WHERE key LIKE ?;',
        [like]
    )
    return (result?.values ?? []).map((row) => {
        let parsed = null
        try { parsed = JSON.parse(row.value) } catch {}
        return {
            key: row.key,
            value: parsed,
            updated_at: Number(row.updated_at) || null,
        }
    })
}

export async function getKeyStats({ prefix = '' } = {}) {
    await ensureKvSchema()
    const like = prefix ? `${prefix}%` : '%'
    const result = await dbQuery(
        `SELECT COUNT(*) as count, SUM(LENGTH(value)) as bytes,
                MIN(updated_at) as oldest, MAX(updated_at) as newest
         FROM kv_store WHERE key LIKE ?;`,
        [like]
    )
    const row = result?.values?.[0] || {}
    return {
        count: Number(row.count) || 0,
        bytes: Number(row.bytes) || 0,
        oldest: Number(row.oldest) || null,
        newest: Number(row.newest) || null,
        now: Math.floor(Date.now() / 1000),
    }
}

export async function pruneKeys({ prefix = '', maxAgeSec } = {}) {
    await ensureKvSchema()

    // Safety: require a prefix when no TTL — prevents accidental full wipe
    const hasValidTTL = Number.isFinite(Number(maxAgeSec)) && Number(maxAgeSec) > 0
    if (!hasValidTTL && !prefix) {
        console.warn('[SQLite] pruneKeys() called without prefix or TTL — ignored for safety')
        return { ok: false, cleared: 0 }
    }

    const like = prefix ? `${prefix}%` : '%'

    // If no maxAgeSec, delete all matching keys (prefix required)
    if (!hasValidTTL) {
        const result = await dbRun('DELETE FROM kv_store WHERE key LIKE ?;', [like])
        return { ok: true, cleared: result?.changes?.changes || 0 }
    }

    const cutoff = Math.floor(Date.now() / 1000) - Math.floor(Number(maxAgeSec))
    const result = await dbRun(
        'DELETE FROM kv_store WHERE key LIKE ? AND updated_at < ?;',
        [like, cutoff]
    )
    return { ok: true, cleared: result?.changes?.changes || 0 }
}

const SQLITE_API = Object.freeze({
    getDb,
    dbRun,
    dbQuery,
    dbExecute,
    initSQLite,
    getSQLiteNamespace,
    getLocalStorageFallback,
    setItem,
    getItem,
    removeItem,
    clearStore,
    listEntries,
    getKeyStats,
    pruneKeys,
})

export default function useSQLite() {
    return SQLITE_API
}
